import React, { useState, useEffect, ChangeEvent } from 'react';
import { useTypedTranslation } from '@app/hooks';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { setExceptionHandler, removeDetailsBreadcrumb, addDetailsBreadcrumb } from '@app/store/actions';
import { PageLoader, PasslyTab, PasslyTabs, PasslyTabContent, PasslyButton, ModalComponent, PasslyTextField } from '@app/components';
import SwipeableViews from 'react-swipeable-views';
import { tenantsService } from '@app/services';
import {
    genericErrorToast,
    booleanToYesNo,
    passlyFormatDate,
    passlyFormatDateWithTimeZoneName,
    DATE_FORMAT,
    DATE_TIME_FULL_FORMAT,
    genericErrorString,
    emptyGuid,
    tenantTypeOptions,
    tenantBetas
} from '@app/helpers';
import { AxiosResponse } from 'axios';
import { ITenant, IAor, ITenantLicenses, IOrganizationShortDetails, TranslationsTypes, IPolicyAuth } from '@app/models';
import { FormControl, FormControlLabel, Checkbox, Link } from '@material-ui/core';
import { toast } from 'react-toastify';
import i18next from 'i18next';
import { RescueTenantDialog } from '@app/pages/systemManager/dialogs/rescueTenant.dialog';
import { EditTenantLicenseDialog } from '@app/pages/systemManager/dialogs/editTenantLicense.dialog';
import { PasslyAutoComplete } from '@app/componentsV2';

interface BetaObject {
    checked: boolean;
    name: string;
    nameTranslated: TranslationsTypes;
}

const TenantDetailsPage = () => {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    const { id: tenantId } = useParams<{ id: string }>();

    const [tabIndex, setTabIndex] = useState(0);
    const [loadingData, setLoadingData] = useState<boolean>(true);
    const [tenantDetails, setTenantDetails] = useState<ITenant>();
    const [aor, setAor] = useState<IAor>();
    const [tenantLicense, setTenantLicense] = useState<ITenantLicenses>();
    const [orgCount, setOrgCount] = useState<number>(0);
    const [tenantOrgs, setTenantOrgs] = useState<IOrganizationShortDetails[]>([]);
    const [tenantAuthPolicy, setTenantAuthPolicy] = useState<IPolicyAuth[]>([]);
    const [betas, setBetas] = useState<BetaObject[]>([]);

    const [isSalesForceInEdit, setIsSalesForceInEdit] = useState<boolean>(false);
    const [salesForceId, setSalesForceId] = useState<{ old: string; new: string }>({
        old: '',
        new: ''
    });
    const [isUsernameInEdit, setIsUsernameInEdit] = useState<boolean>(false);
    const [username, setUsername] = useState<{ old: string; new: string }>({
        old: '',
        new: ''
    });
    const [isTenantTypeEdit, setIsTenantTypeEdit] = useState<boolean>(false);
    const [tenantType, setTenantType] = useState<{ old: string; new: string }>({
        old: '',
        new: ''
    });
    const [openRescueTenant, setOpenRescueTenant] = useState<boolean>(false);
    const [openEditLicense, setOpenEditLicense] = useState<boolean>(false);

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        tenantsService.getTenant(tenantId, handleSuccessGetTenant, genericErrorToast);
        tenantsService.findAor(tenantId, handleSuccessAor, genericErrorToast);
        tenantsService.getTenantLicense(tenantId, handleSuccessLicense, genericErrorToast);
        tenantsService.getTenantOrgs(tenantId, handleSuccessOrgs, genericErrorToast);
        tenantsService.getTenantAuthPolicy(tenantId, handleSuccessAuthPolicy, genericErrorToast);

        return () => {
            dispatch(removeDetailsBreadcrumb());
        };
    }, []);

    const handleSuccessGetTenant = (response: AxiosResponse) => {
        setLoadingData(false);
        const tenant: ITenant = response.data;

        setTenantDetails(tenant);
        setTenantType({ old: tenant.TenantUsingType, new: tenant.TenantUsingType });

        dispatch(addDetailsBreadcrumb(tenant.HomeRealm));

        setSalesForceId({ ...salesForceId, old: tenant.SalesforceId, new: tenant.SalesforceId });

        let betaAttr: string[] = [];
        let betasForDisplay: BetaObject[] = [];

        tenant.Attributes.forEach(function (attr: any) {
            if (attr.Key === 'betas') {
                if (attr.Value.trim() !== '') {
                    betaAttr = JSON.parse(attr.Value);
                }
            }
        });

        // the old code hard codes for password server beta. Moving this code over.
        // if we continue using betas, we need to customize this more.
        let pwdServerBeta: BetaObject = {} as BetaObject;
        pwdServerBeta.name = tenantBetas.PasswordServer;
        pwdServerBeta.nameTranslated = tenantBetas.PasswordServer as TranslationsTypes;
        if (betaAttr.includes(tenantBetas.PasswordServer)) {
            pwdServerBeta.checked = true;
        }

        let eolBannerBeta: BetaObject = {} as BetaObject;
        eolBannerBeta.name = tenantBetas.HideEolBanner;
        eolBannerBeta.nameTranslated = tenantBetas.HideEolBanner as TranslationsTypes;
        if (betaAttr.includes(tenantBetas.HideEolBanner)) {
            eolBannerBeta.checked = true;
        }

        betasForDisplay.push(pwdServerBeta);
        betasForDisplay.push(eolBannerBeta);

        setBetas(betasForDisplay);
    };

    const handleSuccessAor = (response: AxiosResponse) => {
        const aor: IAor = response.data;

        setAor(aor);

        setUsername({ ...username, old: aor.PrincipalName, new: aor.PrincipalName });
    };

    const handleSuccessLicense = (response: AxiosResponse) => {
        const license: ITenantLicenses = response.data;

        setTenantLicense(license);
    };

    const handleSuccessOrgs = (response: AxiosResponse) => {
        const orgs: IOrganizationShortDetails[] = response.data.value;

        setOrgCount(orgs.length);
        setTenantOrgs(orgs);
    };

    const handleSuccessAuthPolicy = (response: AxiosResponse) => {
        const policy: IPolicyAuth[] = response.data.value;

        setTenantAuthPolicy(policy);
    };

    const handleChange = (event: ChangeEvent<{}>, newValue: number) => {
        setTabIndex(newValue);
    };

    const handleChangeIndex = (index: number) => {
        setTabIndex(index);
    };

    const handleBetaCheck = (betaName: string) => {
        let optins: string[] = [];

        betas.forEach(function (b: BetaObject) {
            if (b.name === betaName) {
                b.checked = !b.checked;
            }

            if (b.checked) {
                optins.push(b.name);
            }
        });

        tenantsService.updateBetaOptIns(tenantId, optins, genericErrorToast, handleBetaSaveSuccess);
    };

    const handleBetaSaveSuccess = () => {
        toast(translation('SUCCESS_UPDATE_FIELD'));
    };

    const displayJsonOnScreen = (json: string) => {
        if (!json) {
            return '';
        }

        const obj: any = JSON.parse(json);

        return JSON.stringify(obj, null, 2);
    };

    const resetPolicyClicked = (policyId: string) => {
        tenantsService.resetPolicy(tenantId, emptyGuid, policyId, genericErrorToast, handleResetPolicySuccess);
    };

    const handleResetPolicySuccess = () => {
        toast(translation('POLICY_UPDATED'));
    };

    const handleChangeSalesForceId = (event: React.ChangeEvent<{ value: unknown }>): void => {
        let value: string = event.target.value as string;
        setSalesForceId({ ...salesForceId, new: value });
    };

    const handleCancelSalesForceId = () => {
        setSalesForceId({ ...salesForceId, new: salesForceId.old });
        setIsSalesForceInEdit(false);
    };

    const handleSaveSalesForceId = () => {
        const handleSuccess = (): void => {
            setSalesForceId({ ...salesForceId, old: salesForceId.new });
            setIsSalesForceInEdit(false);
        };

        tenantsService.updateSalesForceId(tenantId, salesForceId.new, genericErrorToast, handleSuccess);
    };

    const handleChangeUsername = (event: React.ChangeEvent<{ value: unknown }>): void => {
        let value: string = event.target.value as string;
        setUsername({ ...username, new: value });
    };

    const handleCancelUsername = () => {
        setUsername({ ...username, new: username.old });
        setIsUsernameInEdit(false);
    };

    const handleSaveUsername = () => {
        if (username.new.trim() === '') {
            toast.error(translation('REQUIRED'));
            return;
        }

        const handleSuccess = (): void => {
            setUsername({ ...username, old: username.new });
            setIsUsernameInEdit(false);
        };

        const handleError = (response: any): void => {
            const error = genericErrorString(response);

            if (error === 'AOR_MUST_BE_EXISTING_USER') {
                toast.error(i18next.t('AOR_MUST_BE_EXISTING_USER'));
            } else {
                toast.error(error);
            }
        };

        tenantsService.updateAorUser(tenantId, username.new, handleError, handleSuccess);
    };

    const handleChangeTenantType = (event: React.ChangeEvent<{ value: { value: string; label: string } }>): void => {
        let value = event.target.value;

        setTenantType({ ...tenantType, new: value.value });
    };

    const handleCancelTenantType = () => {
        setTenantType({ ...tenantType, new: tenantType.old });
        setIsTenantTypeEdit(false);
    };

    const handleSaveTenantType = () => {
        const handleSuccess = (): void => {
            setTenantType({ ...tenantType, old: tenantType.new });
            setIsTenantTypeEdit(false);
        };

        tenantsService.updateTenantType(tenantId, tenantType.new, genericErrorToast, handleSuccess);
    };

    const handleModalOpenCloseCallbackRescue = () => {
        setOpenRescueTenant(!openRescueTenant);
    };

    const handleModalOpenCloseCallbackEditLicense = (resetPage: boolean) => {
        setOpenEditLicense(!openEditLicense);

        if (resetPage) {
            tenantsService.getTenantLicense(tenantId, handleSuccessLicense, genericErrorToast);
        }
    };

    return (
        <>
            <ModalComponent
                headerTitle={translation('RESCUE_TENANT')}
                modalSwitcherCallback={handleModalOpenCloseCallbackRescue}
                form={<RescueTenantDialog dialogOpen={handleModalOpenCloseCallbackRescue} aorEmail={aor?.EmailAddress} tenantId={tenantId} />}
                localModalSwitcher={openRescueTenant}
            />

            <ModalComponent
                headerTitle={translation('EDIT_TENANT_LICENSE')}
                modalSwitcherCallback={handleModalOpenCloseCallbackEditLicense}
                form={<EditTenantLicenseDialog dialogOpen={handleModalOpenCloseCallbackEditLicense} tenantId={tenantId} license={tenantLicense!} />}
                localModalSwitcher={openEditLicense}
            />

            {loadingData && <PageLoader />}

            {!loadingData && (
                <>
                    <PasslyTabs className='detailPage__section-head tab-labels' value={tabIndex} onChange={handleChange}>
                        <PasslyTab className='tab__label' label={translation('TENANT_DETAILS')} key={1} />
                        <PasslyTab className='tab__label' label={translation('AUTHENTICATION_POLICIES')} key={2} />
                    </PasslyTabs>

                    <SwipeableViews style={{ height: 'auto' }} className='tab-contents' index={tabIndex} onChangeIndex={handleChangeIndex}>
                        <PasslyTabContent value={tabIndex} index={0} key={0}>
                            <>
                                <div className='full_width detailPage'>
                                    <div className='detailPage__section'>
                                        <div className='detailPage__section-content'>
                                            <ul className='detailPage__list'>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('HOME_REALM')}</span>
                                                    <span className='detailPage__list--secondary-label'>{tenantDetails?.HomeRealm}</span>
                                                </li>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('IS_SYSTEM_TENANT')}</span>
                                                    <span className='detailPage__list--secondary-label'>
                                                        {tenantDetails && translation(booleanToYesNo(tenantDetails?.IsSystemTenant))}
                                                    </span>
                                                </li>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('TENANT_STATE')}</span>
                                                    <span className='detailPage__list--secondary-label'>{tenantDetails?.ProvisioningState}</span>
                                                </li>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('TENANT_ID')}</span>
                                                    <span className='detailPage__list--secondary-label'>{tenantDetails?.Id}</span>
                                                </li>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('SALESFORCE_ID')}</span>
                                                    <span className='detailPage__list--secondary-label'>
                                                        {!isSalesForceInEdit ? (
                                                            <>
                                                                <span>{salesForceId.old}</span>
                                                                <PasslyButton
                                                                    variant='text'
                                                                    color='primary'
                                                                    onClick={() => setIsSalesForceInEdit(true)}
                                                                >
                                                                    {translation('EDIT')}
                                                                </PasslyButton>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <PasslyTextField
                                                                    fullWidth
                                                                    inlineedit
                                                                    onClickSave={handleSaveSalesForceId}
                                                                    onClickDiscard={handleCancelSalesForceId}
                                                                    label=''
                                                                    id='salesForceId'
                                                                    buttonWidth={26}
                                                                    buttonFontSize={'medium'}
                                                                    onChange={handleChangeSalesForceId}
                                                                    type='text'
                                                                    value={salesForceId.new}
                                                                    autoFocus
                                                                />
                                                            </>
                                                        )}
                                                    </span>
                                                </li>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('TENANT_K1')}</span>
                                                    <span className='detailPage__list--secondary-label'>
                                                        {translation(booleanToYesNo(tenantDetails?.TenantType === 'K1'))}
                                                    </span>
                                                </li>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('TENANT_MME_MSP')}</span>
                                                    <span className='detailPage__list--secondary-label'>
                                                        {!isTenantTypeEdit ? (
                                                            <>
                                                                <span>{tenantType.old}</span>
                                                                <PasslyButton
                                                                    variant='text'
                                                                    color='primary'
                                                                    onClick={() => setIsTenantTypeEdit(true)}
                                                                >
                                                                    {translation('EDIT')}
                                                                </PasslyButton>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <PasslyAutoComplete
                                                                    inlineedit
                                                                    items={tenantTypeOptions}
                                                                    onChange={(event: any) => handleChangeTenantType(event)}
                                                                    id='tenantType'
                                                                    name='tenantType'
                                                                    label={''}
                                                                    value={tenantType.new}
                                                                    buttonWidth={26}
                                                                    buttonFontSize={'medium'}
                                                                    onClickSave={() => handleSaveTenantType()}
                                                                    onClickDiscard={() => handleCancelTenantType()}
                                                                    field='label'
                                                                    valueField='value'
                                                                    dropdown={true}
                                                                />
                                                            </>
                                                        )}
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                        <div className='detailPage__section-head'>{translation('ACCOUNT_OF_RECORD')}</div>
                                        <div className='detailPage__section-content'>
                                            <ul className='detailPage__list'>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('NAME')}</span>
                                                    <span className='detailPage__list--secondary-label'>{aor?.DisplayName}</span>
                                                </li>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('EMAIL_ADDR')}</span>
                                                    <span className='detailPage__list--secondary-label'>
                                                        {aor?.EmailAddress}&nbsp;
                                                        <Link id='rescueTenant' onClick={() => handleModalOpenCloseCallbackRescue()}>
                                                            {translation('RESCUE_TENANT')}
                                                        </Link>
                                                    </span>
                                                </li>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('USERNAME')}</span>
                                                    <span className='detailPage__list--secondary-label'>
                                                        {!isUsernameInEdit ? (
                                                            <>
                                                                <span>{username.old}</span>
                                                                <PasslyButton
                                                                    variant='text'
                                                                    color='primary'
                                                                    onClick={() => setIsUsernameInEdit(true)}
                                                                >
                                                                    {translation('EDIT')}
                                                                </PasslyButton>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <PasslyTextField
                                                                    fullWidth
                                                                    inlineedit
                                                                    onClickSave={handleSaveUsername}
                                                                    onClickDiscard={handleCancelUsername}
                                                                    label=''
                                                                    id='username'
                                                                    buttonWidth={26}
                                                                    buttonFontSize={'medium'}
                                                                    onChange={(event: any) => handleChangeUsername(event)}
                                                                    type='text'
                                                                    value={username.new}
                                                                    autoFocus
                                                                />
                                                            </>
                                                        )}
                                                    </span>
                                                </li>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('USERS_LAST_LOGON')}</span>
                                                    <span className='detailPage__list--secondary-label'>
                                                        {aor &&
                                                            (aor.LastSignedIn === null
                                                                ? translation('DATE_NEVER')
                                                                : passlyFormatDate(aor.LastSignedIn, DATE_TIME_FULL_FORMAT) +
                                                                  ' ' +
                                                                  passlyFormatDateWithTimeZoneName(aor.LastSignedIn))}
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                        <div className='detailPage__section-head'>{translation('TENANT_LICENSE')}</div>
                                        <div className='detailPage__section-content'>
                                            <ul className='detailPage__list'>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>
                                                        <Link id='editTenantLicense' onClick={() => handleModalOpenCloseCallbackEditLicense(false)}>
                                                            {translation('EDIT')}
                                                        </Link>
                                                    </span>
                                                </li>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('TENANT_LICENSE_STATE')}</span>
                                                    <span className='detailPage__list--secondary-label'>{tenantLicense?.State}</span>
                                                </li>
                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('TENANTS_LICENSE_EXPIRY')}</span>
                                                    <span className='detailPage__list--secondary-label'>
                                                        {tenantLicense && passlyFormatDate(tenantLicense.Expiry, DATE_FORMAT)}
                                                    </span>
                                                </li>

                                                {tenantLicense?.Entries.map((entry, index) => (
                                                    <li key={index}>
                                                        <span className='detailPage__list--primary-label'>{entry.Name}: </span>
                                                        <span className='detailPage__list--secondary-label'>
                                                            {entry.Active}/{entry.Seats}
                                                        </span>
                                                    </li>
                                                ))}

                                                <li>
                                                    <span className='detailPage__list--primary-label'>{translation('ORGANIZATION_LIMIT')}</span>
                                                    <span className='detailPage__list--secondary-label'>
                                                        {tenantLicense && tenantLicense.OrganizationLimit > 0 && (
                                                            <>
                                                                <span>
                                                                    {orgCount}/{tenantLicense.OrganizationLimit}
                                                                </span>
                                                            </>
                                                        )}
                                                        {tenantLicense && tenantLicense.OrganizationLimit === 0 && (
                                                            <>
                                                                <span>
                                                                    {orgCount}/{translation('UNLIMITED')}
                                                                </span>
                                                            </>
                                                        )}
                                                    </span>
                                                </li>
                                            </ul>
                                        </div>
                                        <div className='detailPage__section-head'>{translation('ORGANIZATIONS')}</div>
                                        <div className='detailPage__section-content'>
                                            <ul>
                                                {tenantOrgs?.map((org, index) => (
                                                    <li key={index}>{org.HomeRealm}</li>
                                                ))}
                                            </ul>
                                        </div>
                                        <div className='detailPage__section-head'>{translation('BETA_FEATURES')}</div>

                                        <div className='detailPage__section-content'>
                                            <ul className='detailPage__list'>
                                                {betas.map((beta, index) => (
                                                    <li key={index}>
                                                        <FormControl>
                                                            <FormControlLabel
                                                                control={
                                                                    <Checkbox
                                                                        color='primary'
                                                                        name='betaCheckbox'
                                                                        value={beta.name}
                                                                        checked={beta.checked}
                                                                        onChange={() => handleBetaCheck(beta.name)}
                                                                        inputProps={{ 'aria-label': 'primary checkbox' }}
                                                                        id='betaCheckbox'
                                                                    />
                                                                }
                                                                label={translation(beta.nameTranslated)}
                                                            />
                                                        </FormControl>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </>
                        </PasslyTabContent>
                        <PasslyTabContent value={tabIndex} index={1} key={2}>
                            <>
                                <div className='full_width detailPage'>
                                    <div className='detailPage__section'>
                                        <div className='detailPage__section-content'>
                                            <ul className='detailPage__list'>
                                                {tenantAuthPolicy?.map((auth, index) => (
                                                    <div key={index}>
                                                        <li>
                                                            <span className='detailPage__list--primary-label'>{translation('NAME')}</span>
                                                            <span className='detailPage__list--secondary-label'>
                                                                {auth.DisplayName}
                                                                <>{auth.IsDefault && <span>({translation('DEFAULT')})</span>}</>
                                                            </span>
                                                        </li>
                                                        {auth.Configuration && (
                                                            <>
                                                                <li>
                                                                    <span className='detailPage__list--primary-label'>
                                                                        {translation('ENHANCED_POLICY')}
                                                                    </span>
                                                                    <Link id='resetPolicy' onClick={() => resetPolicyClicked(auth.Id)}>
                                                                        {translation('RESET_POLICY_TEXT')}
                                                                    </Link>
                                                                </li>
                                                                <li>
                                                                    <pre>
                                                                        <code>{displayJsonOnScreen(auth.Configuration)}</code>
                                                                    </pre>
                                                                </li>
                                                            </>
                                                        )}
                                                    </div>
                                                ))}
                                                ;
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            </>
                        </PasslyTabContent>
                    </SwipeableViews>
                </>
            )}
        </>
    );
};

export default TenantDetailsPage;
